import { useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { useCachedState } from "@/lib/swr";
import { api } from "@/lib/api";
import {
  Landmark, Building2, Home, DollarSign, TrendingUp, Activity,
  ChevronRight, Loader2, Layers, Gauge,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const marginColor = (m) => (m > 0 ? "text-emerald-600" : m < 0 ? "text-redline-600" : "text-ink");

function Kpi({ icon: Icon, label, value, sub, valueClass = "" }) {
  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="flex items-center gap-2 text-muted-foreground text-sm"><Icon className="h-4 w-4" /> {label}</div>
      <div className={`font-heading text-3xl font-extrabold tracking-tight mt-2 ${valueClass}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function Billing() {
  const [data, setData] = useCachedState("billing:cur", null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(null);
  const [openCo, setOpenCo] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = month ? `?month=${month}` : "";
    api.get(`/admin/billing${q}`)
      .then((r) => { if (alive) { setData(r.data); if (!month) setMonth(r.data.month); } })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month]);

  if (loading && !data) {
    return <div className="flex items-center justify-center py-24 text-ink/40" data-testid="billing-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!data) return <div className="py-24 text-center text-ink/40">Couldn't load billing.</div>;

  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <PageTitle title="Billing" about={<>Every management company, their communities, and what they cost vs. earn — {data.month}.</>} />
        </div>
        <select data-testid="billing-month" value={month || data.month} onChange={(e) => setMonth(e.target.value)}
          className="rounded-full bg-white border border-sand-200 px-4 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-redline-500 self-start">
          {data.months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div data-testid="billing-kpis" className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi icon={Landmark} label="Companies" value={t.companies} />
        <Kpi icon={Building2} label="Communities" value={t.communities} sub={t.pending > 0 ? `${t.pending} pending activation` : "all active"} />
        <Kpi icon={Home} label="Homes / units" value={t.homes.toLocaleString()} />
        <Kpi icon={DollarSign} label="Total MRR" value={money(t.revenue)} valueClass="text-emerald-600" sub={`Base ${money(t.base_revenue)} + add-ons ${money(t.addon_revenue)}`} />
        <Kpi icon={Activity} label="Usage cost" value={money(t.usage_cost)} sub={`AI ${money(t.ai_cost)} · SMS ${money(t.sms_cost)} · Voice ${money(t.voice_cost)}`} />
        <Kpi icon={TrendingUp} label="Gross margin" value={money(t.margin)} valueClass={marginColor(t.margin)} />
      </div>

      <div className="flex items-center gap-3 text-sm">
        <Link to="/dashboard/billing?tab=plans" data-testid="billing-link-plans" className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-4 py-2 font-semibold text-ink hover:bg-sand-50 transition-colors">
          <Layers className="h-4 w-4" /> Edit add-ons &amp; prices
        </Link>
        <Link to="/dashboard/billing?tab=usage" data-testid="billing-link-usage" className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-4 py-2 font-semibold text-ink hover:bg-sand-50 transition-colors">
          <Gauge className="h-4 w-4" /> Usage caps &amp; alerts
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="px-5 py-4 border-b border-sand-200"><h2 className="font-heading font-bold text-lg">By management company</h2></div>
        <div className="divide-y divide-sand-100">
          {data.companies.map((g) => {
            const key = g.company_id || "__direct__";
            const open = !!openCo[key];
            return (
              <div key={key} data-testid={`billing-company-${key}`}>
                <button onClick={() => setOpenCo((s) => ({ ...s, [key]: !s[key] }))}
                  data-testid={`billing-company-toggle-${key}`}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-sand-50 transition-colors text-left">
                  <ChevronRight className={`h-4 w-4 text-ink/40 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-ink truncate flex items-center gap-2">
                      {g.company_id ? <Landmark className="h-4 w-4 text-indigo-500 shrink-0" /> : <Building2 className="h-4 w-4 text-ink/40 shrink-0" />}
                      {g.name}
                      {g.pending > 0 && <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase">{g.pending} pending</span>}
                    </div>
                    <div className="text-xs text-ink/50 mt-0.5">
                      {g.communities} communit{g.communities === 1 ? "y" : "ies"}
                      {g.max_communities != null && ` of ${g.max_communities}`} · {g.homes.toLocaleString()} homes
                    </div>
                  </div>
                  <div className="hidden sm:grid grid-cols-3 gap-6 text-right shrink-0">
                    <div><div className="text-[10px] uppercase tracking-wide text-ink/40">MRR</div><div className="font-semibold text-emerald-600">{money(g.revenue)}</div><div className="text-[9px] text-ink/40 tabular-nums">{money(g.base_revenue)} base · {money(g.addon_revenue)} add</div></div>
                    <div><div className="text-[10px] uppercase tracking-wide text-ink/40">Usage</div><div className="font-semibold text-ink/70">{money(g.usage_cost)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wide text-ink/40">Margin</div><div className={`font-bold ${marginColor(g.margin)}`}>{money(g.margin)}</div></div>
                  </div>
                </button>
                {open && (
                  <div className="bg-sand-50/60 px-5 pb-3" data-testid={`billing-company-detail-${key}`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-ink/40 text-[10px] uppercase tracking-wide">
                          <th className="text-left font-semibold py-2">Community</th>
                          <th className="text-right font-semibold py-2 hidden md:table-cell">Homes</th>
                          <th className="text-right font-semibold py-2 hidden md:table-cell">AI</th>
                          <th className="text-right font-semibold py-2 hidden md:table-cell">SMS</th>
                          <th className="text-right font-semibold py-2 hidden md:table-cell">Voice</th>
                          <th className="text-right font-semibold py-2">MRR</th>
                          <th className="text-right font-semibold py-2">Usage</th>
                          <th className="text-right font-semibold py-2 pr-1">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.map((c) => (
                          <tr key={c.tenant_id} data-testid={`billing-row-${c.tenant_id}`} className="border-t border-sand-200/70">
                            <td className="py-2.5 font-medium text-ink">
                              <div className="flex items-center gap-2">
                                {c.name}
                                {c.status === "pending_activation" && <span className="rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[9px] font-bold uppercase">Pending</span>}
                              </div>
                            </td>
                            <td className="py-2.5 text-right text-ink/60 hidden md:table-cell">{c.homes}</td>
                            <td className="py-2.5 text-right text-ink/60 hidden md:table-cell">{money(c.ai_cost)}</td>
                            <td className="py-2.5 text-right text-ink/60 hidden md:table-cell">{money(c.sms_cost)}</td>
                            <td className="py-2.5 text-right text-ink/60 hidden md:table-cell">{money(c.voice_cost)}</td>
                            <td className="py-2.5 text-right font-semibold text-emerald-600">{money(c.revenue)}</td>
                            <td className="py-2.5 text-right text-ink/70">{money(c.usage_cost)}</td>
                            <td className={`py-2.5 text-right font-bold pr-1 ${marginColor(c.margin)}`}>{money(c.margin)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
          {data.companies.length === 0 && <div className="px-5 py-12 text-center text-ink/40">No communities yet.</div>}
        </div>
      </div>
    </div>
  );
}
